import React, { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    Box, 
    Typography, 
    Button, 
    Divider, 
    Chip, 
    Stack,
    Alert, 
    CircularProgress, 
    Table, 
    TableHead, 
    TableRow,
    TableCell, 
    TableBody, 
    TableContainer,
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
    ListSubheader, 
    ListItemText,
} from '@mui/material';
import { 
    AutoFixHigh, 
    ArrowForward, 
    AutoAwesome, 
} from '@mui/icons-material';
import api from 'state/axios';
import { optimizeTailAssignment } from './tailOptimizer';


const ALGORITHMS = [
    {
        group: 'Fast',
        options: [
            {
                value: 'greedy',
                label: 'Greedy',
                description: 'Single-pass greedy, fastest',
                backend: false,
            },
        ],
    },
    {
        group: 'Balanced',
        options: [
            {
                value: 'local-search',
                label: 'Local Search',
                description: 'Greedy + targeted swap repair for disconnections',
                backend: false,
            },
            {
                value: 'sa',
                label: 'Simulated Annealing',
                description: 'Greedy + SA, escapes local optima probabilistically',
                backend: true,
            },
        ],
    },
    {
        group: 'Thorough',
        options: [
            {
                value: 'ga',
                label: 'Genetic Algorithm',
                description: 'Population-based search with crossover & mutation',
                backend: true,
            },
        ],
    },
    {
        group: 'Optimal',
        options: [
            {
                value: 'cp-sat',
                label: 'CP-SAT Solver',
                description: 'Exact ILP solver on Linux · SA fallback on Windows',
                backend: true,
            },
        ],
    },
];

const ENGINE_DISPLAY = {
    'greedy':              { label: 'Greedy',               color: 'default' },
    'local-search':        { label: 'Local Search',         color: 'primary' },
    'sa':                  { label: 'Simulated Annealing',  color: 'primary' },
    'ga':                  { label: 'Genetic Algorithm',    color: 'secondary' },
    'cp-sat':              { label: 'CP-SAT (Exact)',        color: 'success' },
    'simulated-annealing': { label: 'Simulated Annealing',  color: 'primary' },
    'genetic-algorithm':   { label: 'Genetic Algorithm',    color: 'secondary' },
};

const EngineChip = ({ engine }) => {
    if (!engine) return null;
    const { label, color } = ENGINE_DISPLAY[engine] || { label: engine, color: 'default' };
    return (
        <Chip
            size="small"
            icon={<AutoAwesome sx={{ fontSize: 14 }} />}
            label={label}
            color={color}
            variant="outlined"
        />
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const StatBox = ({ label, before, after }) => {
    const improved = after < before;
    const same = after === before;
    return (
        <Box textAlign="center" flex={1}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mt={0.5}>
                <Typography variant="h6" color={before > 0 ? 'error' : 'success.main'}>{before}</Typography>
                <ArrowForward fontSize="small" color="disabled" />
                <Typography variant="h6" color={after > 0 ? (improved ? 'warning.main' : 'error') : 'success.main'}>
                    {after}
                </Typography>
                {!same && (
                    <Chip
                        label={improved ? `−${before - after}` : `+${after - before}`}
                        size="small"
                        color={improved ? 'success' : 'error'}
                        sx={{ ml: 0.5 }}
                    />
                )}
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const BACKEND_ALGORITHMS = new Set(
    ALGORITHMS.flatMap(g => g.options).filter(o => o.backend).map(o => o.value)
);

const OptimizeScheduleDialog = ({ open, onClose, flights, onPreview }) => {
    const [algorithm, setAlgorithm] = useState('local-search');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleCompute = async () => {
        setRunning(true);
        setResult(null);
        setError(null);

        if (BACKEND_ALGORITHMS.has(algorithm)) {
            try {
                const { data } = await api.post('/flights/optimize-tail', { flights, algorithm });
                setResult({ ...data, assignments: new Map(data.assignments) });
            } catch (err) {
                setError(err.response?.data?.message || err.message);
            } finally {
                setRunning(false);
            }
        } else {
            setTimeout(() => {
                try {
                    const r = optimizeTailAssignment(flights, { algorithm, maxIter: 100 });
                    setResult(r);
                } finally {
                    setRunning(false);
                }
            }, 30);
        }
    };

    const handlePreview = () => {
        if (!result) return;
        onPreview(result);
        onClose();
    };

    const handleClose = () => {
        setResult(null);
        setError(null);
        onPreview(null);
        onClose();
    };

    const flightCount = useMemo(
        () => flights.filter(f => f.flightReg && f.acType && f.dep && f.arr).length,
        [flights]
    );

    const { stats, changes } = result || {};
    const noChange = result && changes.length === 0;
    const allFixed = result && stats?.after?.disconnections === 0 && stats?.after?.shortTurnarounds === 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <AutoFixHigh color="primary" />
                    <Typography variant="h6">Optimize Tail Assignment</Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {/* Algorithm selector */}
                    <FormControl size="small" fullWidth>
                        <InputLabel>Algorithm</InputLabel>
                        <Select
                            value={algorithm}
                            label="Algorithm"
                            onChange={e => {
                                setAlgorithm(e.target.value);
                                setResult(null);
                                setError(null);
                            }}
                        >
                            {ALGORITHMS.flatMap(({ group, options }) => [
                                <ListSubheader
                                    key={group}
                                    sx={{ lineHeight: '28px', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase' }}
                                >
                                    {group}
                                </ListSubheader>,
                                ...options.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} dense>
                                        <ListItemText
                                            primary={opt.label}
                                            secondary={opt.description}
                                            slotProps={{
                                                primary: { variant: 'body2', fontWeight: algorithm === opt.value ? 600 : 400 },
                                                secondary: { variant: 'caption' },
                                            }}
                                        />
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary">
                        {flightCount} eligible flights
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}

                    {running && (
                        <Box display="flex" alignItems="center" gap={1} py={1}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Computing optimal schedule…
                            </Typography>
                        </Box>
                    )}

                    {result && !running && (
                        <>
                            <Divider />

                            <Box>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="subtitle2">Results</Typography>
                                    <EngineChip engine={stats?.engine} />
                                </Box>
                                <Box display="flex" gap={2} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <StatBox
                                        label="Disconnections"
                                        before={stats.before.disconnections}
                                        after={stats.after.disconnections}
                                    />
                                    <Divider orientation="vertical" flexItem />
                                    <StatBox
                                        label="Short TAT"
                                        before={stats.before.shortTurnarounds}
                                        after={stats.after.shortTurnarounds}
                                    />
                                    <Divider orientation="vertical" flexItem />
                                    <Box textAlign="center" flex={1}>
                                        <Typography variant="caption" color="text.secondary">Tail Changes</Typography>
                                        <Typography variant="h6" color="primary" mt={0.5}>{stats.changed}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {noChange && (
                                <Alert severity="success">
                                    No tail reassignments needed — schedule is already optimal.
                                </Alert>
                            )}
                            {allFixed && !noChange && (
                                <Alert severity="success">
                                    All violations resolved with {stats.changed} tail swap{stats.changed > 1 ? 's' : ''}.
                                </Alert>
                            )}
                            {!noChange && stats.after.disconnections > 0 && (
                                <Alert severity="warning">
                                    {stats.after.disconnections} disconnection{stats.after.disconnections > 1 ? 's' : ''} remain
                                    — may require manual reassignment or additional aircraft.
                                </Alert>
                            )}

                            {changes.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Proposed Changes ({changes.length})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Flight</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>ETD</TableCell>
                                                    <TableCell>Sector</TableCell>
                                                    <TableCell>Current Tail</TableCell>
                                                    <TableCell sx={{ px: 0.5 }} />
                                                    <TableCell>New Tail</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {changes.map(c => (
                                                    <TableRow key={c.id} hover>
                                                        <TableCell>
                                                            <Typography variant="body2">{c.flightDesc}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {c.flightDate ? format(parseISO(c.flightDate), 'dd/MM') : '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {c.etd ? c.etd.substring(0, 5) : '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">{c.dep}→{c.arr}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={c.from} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell sx={{ px: 0.5 }}>
                                                            <ArrowForward fontSize="small" color="primary" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={c.to} size="small" color="primary" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleClose} 
                    disabled={running}
                >
                    Cancel
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleCompute}
                    disabled={running || !flightCount}
                    startIcon={running ? <CircularProgress size={14} color="inherit" /> : null}
                >
                    {running ? 'Computing…' : 'Compute'}
                </Button>
                <Button
                    variant="outlined"
                    color="success"
                    onClick={handlePreview}
                    disabled={!result || !changes?.length || running}
                >
                    Preview
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OptimizeScheduleDialog;
